import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAdminPageSidebar } from '@atlassian/jira-admin-pages-common/src/controllers/sidebar/index.tsx';
import AdminSidebarHeader from '@atlassian/jira-admin-pages-common/src/ui/sidebar/header.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type {
	PriorityCurrentSidebarState,
	CreatePrioritySidebar,
	EditPrioritySidebar,
	CreateAndEditPriorityRequestBody,
	Priority,
	PriorityFormAsyncErrors,
} from '../../common/types';
import { PriorityForm, PriorityFooter } from './form/form-fields';
import { messages } from './messages';

type PrioritySidebarProps = {
	currentSidebar: PriorityCurrentSidebarState;
	asyncFormErrors?: PriorityFormAsyncErrors;
	onClose: () => void;
	onCreate: (formData: CreateAndEditPriorityRequestBody) => void;
	onEdit: (formData: CreateAndEditPriorityRequestBody, priority?: Priority) => void;
};

const isCreatePrioritySidebar = (
	currentSidebar: PriorityCurrentSidebarState,
): currentSidebar is CreatePrioritySidebar => currentSidebar?.sidebarName === 'CREATE';

const isEditPrioritySidebar = (
	currentSidebar: PriorityCurrentSidebarState,
): currentSidebar is EditPrioritySidebar => currentSidebar?.sidebarName === 'EDIT';

type ShowLoadingBarProps = {
	currentSidebar: PriorityCurrentSidebarState;
	savedSidebar: PriorityCurrentSidebarState;
};
const showLoadingBar = ({ currentSidebar, savedSidebar }: ShowLoadingBarProps) =>
	isEditPrioritySidebar(currentSidebar) && isEditPrioritySidebar(savedSidebar)
		? currentSidebar?.priority !== savedSidebar?.priority
		: currentSidebar?.sidebarName !== savedSidebar?.sidebarName;

export const PrioritySidebarSwitcher = ({
	currentSidebar,
	asyncFormErrors,
	onClose,
	onEdit,
	onCreate,
}: PrioritySidebarProps) => {
	const { formatMessage } = useIntl();
	const { setSidebarContent, closeSidebar } = useAdminPageSidebar();
	const previousSidebar = useRef<PriorityCurrentSidebarState>(null);
	const [savedSidebar, setSavedSidebar] = useState<PriorityCurrentSidebarState>(null);

	const showCreateSidebar = useCallback(
		(createPrioritySidebar: CreatePrioritySidebar) =>
			setSidebarContent({
				header: (
					<AdminSidebarHeader title={formatMessage(messages.addPriorityHeader)} onClose={onClose} />
				),
				content: <PriorityForm asyncFormErrors={asyncFormErrors} onHandleSubmit={onCreate} />,
				footer: (
					<PriorityFooter
						onCancel={onClose}
						confirmText={formatMessage(messages.addPriorityButton)}
						submitting={createPrioritySidebar.submitting}
					/>
				),
				loadingBar: showLoadingBar({ currentSidebar, savedSidebar }),
			}),
		[
			asyncFormErrors,
			onClose,
			onCreate,
			formatMessage,
			setSidebarContent,
			currentSidebar,
			savedSidebar,
		],
	);

	const showEditSidebar = useCallback(
		(editPrioritySidebar: EditPrioritySidebar) =>
			setSidebarContent({
				header: (
					<AdminSidebarHeader
						title={formatMessage(messages.editPriorityHeader)}
						onClose={onClose}
					/>
				),
				content: (
					<PriorityForm
						asyncFormErrors={asyncFormErrors}
						initialPriority={editPrioritySidebar.priority}
						onHandleSubmit={onEdit}
					/>
				),
				footer: (
					<PriorityFooter
						onCancel={onClose}
						confirmText={formatMessage(messages.editPriorityButton)}
						submitting={editPrioritySidebar.submitting}
					/>
				),
				loadingBar: showLoadingBar({ currentSidebar, savedSidebar }),
			}),
		[
			asyncFormErrors,
			onClose,
			onEdit,
			formatMessage,
			setSidebarContent,
			currentSidebar,
			savedSidebar,
		],
	);

	useEffect(() => {
		setSavedSidebar(previousSidebar.current);
		previousSidebar.current = currentSidebar;
	}, [currentSidebar]);

	useEffect(() => {
		if (isCreatePrioritySidebar(currentSidebar)) {
			showCreateSidebar(currentSidebar);
		} else if (isEditPrioritySidebar(currentSidebar)) {
			showEditSidebar(currentSidebar);
		} else {
			closeSidebar();
		}
	}, [currentSidebar, closeSidebar, showCreateSidebar, showEditSidebar]);

	return null;
};
